import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';

import { ReactComponent as ClosedEyeIcon } from '@/assets/icons/closed-eye.svg';
import { ReactComponent as EyeIcon } from '@/assets/icons/eye.svg';
import FancySwitch from '@/components/FancySwitch';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useToasts from '@/hooks/useToasts';
import { Actions, ToggleButton } from '@/pages/MLProject/components/Card';
import {
  MLProjectTranslationScriptPart,
  MediaResource,
  VoiceoverMedia,
} from '@/types/resources';
import { api } from '@/utils/api';
import * as storage from '@/utils/storage';
import { hexToRgba } from '@/utils/theme';

import VideoAudioPreview from './VideoAudioPreview';
import VoiceoverSettings from './VoiceoverSettings';

const Card = styled.div`
  background: ${({ theme }) => theme.colors.grayscaleLight};
  border-radius: 0 0 4px 4px;
`;

const Header = styled.div`
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

const Footer = styled.div`
  padding: 24px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 0 0 4px 4px;
`;

const FooterTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const FooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SubtitlesSwitch = styled(FancySwitch)`
  background: ${({ theme }) => theme.colors.white};
  padding-top: 4px;
  padding-bottom: 4px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

const GenerateButton = styled(Text)`
  height: 34px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  border-radius: 4px;
  background: ${({ theme }) => hexToRgba(theme.colors.purple, 0.15)};
  transition: 0.15s ease-in-out all;

  &:hover {
    background: ${({ theme }) => theme.colors.purple};
    color: ${({ theme }) => theme.colors.white};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.lightGray};
    color: ${({ theme }) => theme.colors.gray};
    cursor: not-allowed;
    ${Text} {
      color: ${({ theme }) => theme.colors.gray};
    }
  }
`;

export const OnOffButton = styled.button<{ $large?: boolean }>`
  display: flex;
  padding: 4px;
  align-items: center;
  height: 34px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  transition: border-color ease-out 0.2s;

  ${Text} {
    margin: 0 16px;
  }

  &:hover {
    border-color: rgba(34, 34, 34, 0.25);
  }

  &:disabled {
    cursor: not-allowed;
    ${Text} {
      color: ${({ theme }) => theme.colors.gray};
    }
  }

  ${({ $large = false }) =>
    $large &&
    css`
      height: 38px;
      padding: 7px;
    `}
`;

interface Props {
  translationId: number;
  part: MLProjectTranslationScriptPart;
  voiceoverMedia?: VoiceoverMedia;
  videoMedia: MediaResource;
  isFirst: boolean;
  isDisabled: boolean;
  videoVolume: number;
}

const VoiceoverCard: FC<Props> = ({
  translationId,
  part,
  voiceoverMedia,
  videoMedia,
  isFirst,
  videoVolume,
  // isDisabled,
}) => {
  const theme = useTheme();
  const toasts = useToasts();
  const alerts = useAlerts();
  const queryClient = useQueryClient();

  const [isOpened, setIsOpened] = useState(isFirst);
  const [isTogglingReuse, setIsTogglingReuse] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  // const [isApproving, setIsApproving] = useState(false);

  const toggleIsOpened = () => setIsOpened((prev) => !prev);

  const onGenerate = async () => {
    if (!part.reuseVoiceoverSettings && !part.voiceoverSettings.voiceId) {
      return alerts.error('Please select voice and save voiceover settings!');
    }

    if (!part.selectedTranslation) {
      return alerts.error('Please choose either translation A or B');
    }

    setIsGenerating(true);

    const toastId = toasts.create('loading', 'Generating...');

    try {
      const res = await api.post<never, { error?: string }>(
        `/v1/distribution/ml-translations/${translationId}/parts/${part.uuid}/voiceovers`,
      );

      if (res.error) {
        alerts.error(res.error);
      } else {
        await queryClient.invalidateQueries([
          'ml_translations',
          {
            id: translationId.toString(),
          },
        ]);

        toasts.success(toastId, 'Generated');
      }
    } catch {
      toasts.error(toastId, 'An error occured!');
    } finally {
      setIsGenerating(false);
    }
  };

  const onSubtitlesToggle = async () => {
    const toastId = toasts.create('loading', 'Loading...');

    try {
      await api.put(`/v1/distribution/ml-translations/${translationId}/parts`, {
        applySubtitles: !part.applySubtitles,
        uuid: part.uuid,
      });

      await queryClient.invalidateQueries([
        'ml_translations',
        {
          id: translationId.toString(),
        },
      ]);

      toasts.success(toastId, 'Saved');

      if (!part.applySubtitles) {
        storage.set(
          `ml_translation_${translationId}_subs_styles_card_visibility`,
          true,
        );
      }
    } catch {
      toasts.error(toastId, 'An Error Occured!');
    }
  };

  // const onApprove = async () => {
  //   setIsApproving(true);

  //   const toastId = toasts.create('loading', 'Loading...');

  //   try {
  //     const { error } = await api.post<never, { error?: string }>(
  //       `/v1/distribution/ml-translations/${translationId}/parts/${part.uuid}/approve`,
  //     );

  //     if (error) {
  //       alerts.error(error);
  //     } else {
  //       await queryClient.invalidateQueries([
  //         'ml_translations',
  //         {
  //           id: translationId.toString(),
  //         },
  //       ]);

  //       toasts.success(toastId, 'Saved');
  //     }
  //   } catch {
  //     toasts.error(toastId, 'An error occurred!');
  //   } finally {
  //     setIsApproving(false);
  //   }
  // };

  const onToggleReuseSettings = async () => {
    setIsTogglingReuse(true);
    const toastId = toasts.create('loading', 'Loading');
    const newValue = !part.reuseVoiceoverSettings;

    try {
      await api.put(`/v1/distribution/ml-translations/${translationId}/parts`, {
        uuid: part.uuid,
        reuseVoiceoverSettings: newValue,
      });

      await queryClient.invalidateQueries([
        'ml_translations',
        {
          id: translationId.toString(),
        },
      ]);

      setIsOpened(!newValue);
      toasts.success(toastId, 'Saved');
    } catch {
      toasts.error(toastId, 'Error!');
    } finally {
      setIsTogglingReuse(false);
    }
  };

  // const isApprovalDisabled =
  //   (!voiceoverMedia && !part.isApproved) || isDisabled || isApproving;

  // const isVoiceoverGenerationDisabled = part.isApproved || isGenerating;
  const isVoiceoverGenerationDisabled = isGenerating;

  return (
    <Card>
      <Header>
        <div>
          <Text $type="caption" as="span">
            Voiceover & Subtitles
          </Text>{' '}
          <Text $type="caption" $color="gray" as="span">
            ({part.timestamp})
          </Text>
        </div>

        <Actions>
          {!isFirst && (
            <OnOffButton
              $large
              type="button"
              onClick={onToggleReuseSettings}
              disabled={isTogglingReuse || isVoiceoverGenerationDisabled}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  width="24"
                  height="24"
                  rx="4"
                  fill={
                    part.reuseVoiceoverSettings
                      ? theme.colors.success
                      : theme.colors.grayscale
                  }
                />
                <path
                  d="M15 10.332L11 14.332L9 12.332"
                  stroke={
                    part.reuseVoiceoverSettings
                      ? theme.colors.white
                      : theme.colors.gray
                  }
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <Text as="span" $type="smallHeadline">
                Reuse Settings
              </Text>
            </OnOffButton>
          )}
          <ToggleButton
            $closed={!isOpened}
            onClick={toggleIsOpened}
            disabled={part.reuseVoiceoverSettings}
          >
            {isOpened ? <EyeIcon /> : <ClosedEyeIcon />}
          </ToggleButton>
        </Actions>
      </Header>
      {isOpened && (
        <VoiceoverSettings
          partUuid={part.uuid}
          translationId={translationId}
          settings={part.voiceoverSettings}
          // disabled={part.isApproved}
          disabled={false}
          closeTab={() => setIsOpened(false)}
        />
      )}
      <Footer>
        <FooterTop>
          <div>
            <Text $type="caption" as="span" $color="gray">
              Timestamp ·
            </Text>{' '}
            <Text $type="caption" as="span">
              {part.timestamp}
            </Text>
          </div>
          <FooterActions>
            <GenerateButton
              as="button"
              $type="smallHeadline"
              $color="purple"
              onClick={onGenerate}
              disabled={isVoiceoverGenerationDisabled}
            >
              {voiceoverMedia ? 'Regenerate' : 'Generate'}
            </GenerateButton>
            <SubtitlesSwitch
              text="Add Subtitles:"
              isActive={!!part.applySubtitles}
              onClick={onSubtitlesToggle}
              // disabled={part.isApproved}
            />
            {/* <OnOffButton
              onClick={onApprove}
              type="button"
              disabled={isApprovalDisabled}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  width="24"
                  height="24"
                  rx="4"
                  fill={
                    part.isApproved
                      ? theme.colors.success
                      : theme.colors.grayscale
                  }
                />
                <path
                  d="M15 10.332L11 14.332L9 12.332"
                  stroke={
                    part.isApproved ? theme.colors.white : theme.colors.gray
                  }
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <Text as="span" $type="smallHeadline">
                {part.isApproved ? 'Unmark Approval' : 'Mark as Approved'}
              </Text>
            </OnOffButton> */}
          </FooterActions>
        </FooterTop>
        {!!voiceoverMedia && (
          <VideoAudioPreview
            voiceoverMedia={voiceoverMedia}
            videoMedia={videoMedia}
            timestamp={part.timestamp}
            videoVolume={videoVolume}
          />
        )}
      </Footer>
    </Card>
  );
};

export default VoiceoverCard;
